import {Box, Flex, Text, useTheme} from '@chakra-ui/react';
import React from 'react';
import {ChatComment, MENTION_REGEXP, getUserHandle} from '.';
import {TextG12, TextG14} from '../../typography';
import {formatUserName} from '.';

interface ReplyPreviewProps {
  comment: ChatComment;
  onClose?: () => void;
  isInMessage?: boolean; // If true, this is displayed within a message
  onClick?: () => void; // For scrolling to original message
}

const ReplyPreview = ({comment, onClose, isInMessage = false, onClick}: ReplyPreviewProps) => {
  const theme = useTheme();
  const displayName = formatUserName(comment.user);
  const displayNameWithFirstName = comment.user?.name ? `${comment.user.name} (${displayName})` : displayName;

  // Parse mentions to show @handle instead of @id[handle]
  const parsedContent = comment.content.replace(MENTION_REGEXP, (match) =>
    match.replace(MENTION_REGEXP, `@${getUserHandle(match, comment).handle}`)
  );

  // Truncate content if too long
  const maxLength = isInMessage ? 100 : 150;
  const truncatedContent =
    parsedContent.length > maxLength
      ? parsedContent.substring(0, maxLength) + '...'
      : parsedContent;

  return (
    <Flex
      bg={isInMessage ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.08)'}
      borderLeft={`3px solid ${theme.colors.grey}`}
      borderRadius="6px"
      px={isInMessage ? 2.5 : 3}
      py={isInMessage ? 1.5 : 2}
      mb={isInMessage ? 0 : 0}
      gap={2}
      align="center"
      onClick={onClick}
      position="relative">
      <Flex flexDir="column" flex={1} gap={0.5} overflow="hidden">
        <TextG12 fontWeight="600" color={theme.colors.lightBlack} noOfLines={1}>
          {displayNameWithFirstName}
        </TextG12>
        <TextG12
          color={theme.colors.grey}
          noOfLines={isInMessage ? 2 : 3}
          whiteSpace="pre-wrap"
          wordBreak="break-word">
          {truncatedContent}
        </TextG12>
      </Flex>
      {onClose && (
        <Box
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          color={theme.colors.grey}
          _hover={{color: theme.colors.lightBlack}}>
          ✕
        </Box>
      )}
    </Flex>
  );
};

export default ReplyPreview;
